import { Home, Ticket, Network, Building } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import type { NavItem, DropdownNavItem } from "../hooks/useNavState";
import {
	getSidebarTheme,
	isDarkVariant,
	type SidebarVariant,
} from "./sidebarTheme";
import { ROUTES } from "@/app/routes";
import { SidebarDropdownItem } from "./SidebarDropdownItem";

interface SidebarNavigationProps {
	isCollapsed: boolean;
	onExpand: () => void;
	variant?: SidebarVariant;
	navItems?: NavItem[];
	dropdownItems?: DropdownNavItem[];
}

const EMPTY_NAV_ITEMS: NavItem[] = [];
const EMPTY_DROPDOWN_ITEMS: DropdownNavItem[] = [];

export const SidebarNavigation = ({
	isCollapsed,
	onExpand,
	variant = "default",
	navItems = EMPTY_NAV_ITEMS,
	dropdownItems = EMPTY_DROPDOWN_ITEMS,
}: SidebarNavigationProps) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const theme = getSidebarTheme(variant);
	const isDark = isDarkVariant(variant);

	const defaultNavItems: NavItem[] =
		navItems.length > 0
			? navItems
			: [
					{
						path: ROUTES.DASHBOARD,
						icon: <Home size={16} />,
						label: t("nav.dashboard"),
					},
				];

	const defaultOperationsDropdowns: DropdownNavItem[] =
		dropdownItems.length > 0
			? dropdownItems
			: [
					{
						path: ROUTES.PROJECTS.LIST,
						icon: <Ticket size={16} />,
						label: t("nav.clients"),
						children: [
							{ path: ROUTES.PROMOTERS.LIST, label: t("nav.promoters") },
							{ path: ROUTES.PROJECTS.LIST, label: t("nav.projects") },
						],
					},
					{
						path: ROUTES.INVESTMENTS.MAIN,
						icon: <Network size={16} />,
						label: t("nav.investments"),
						children: [
							{
								path: ROUTES.INVESTMENTS.CALCULATOR,
								label: t("nav.calculator"),
							},
							{ path: ROUTES.INVESTMENTS.INVESTORS, label: t("nav.investors") },
							{ path: ROUTES.INVESTMENTS.MAIN, label: t("nav.investments") },
							{ path: ROUTES.INVESTMENTS.CONTRACTS, label: t("nav.contracts") },
						],
					},
				];

	const defaultManagementDropdowns: DropdownNavItem[] = [
		{
			path: ROUTES.ORGANIZATION.GENERAL,
			icon: <Building size={16} />,
			label: t("nav.organization"),
			children: [
				{
					path: "/organization/general",
					label: t("organization.tabs.general"),
				},
				{
					path: ROUTES.ORGANIZATION.USERS,
					label: t("organization.tabs.users"),
				},
				{
					path: ROUTES.ORGANIZATION.TEMPLATES,
					label: t("organization.tabs.templates"),
				},
			],
		},
	];

	const isSimplifiedNav = variant === "admin" || variant === "hub";
	const itemsToUse = isSimplifiedNav ? navItems : defaultNavItems;
	const operationsDropdowns = isSimplifiedNav ? [] : defaultOperationsDropdowns;
	const managementDropdowns = isSimplifiedNav ? [] : defaultManagementDropdowns;

	const [expandedDropdowns, setExpandedDropdowns] = useState<
		Record<string, boolean>
	>(() => {
		const initialExpanded: Record<string, boolean> = {};
		[...operationsDropdowns, ...managementDropdowns].forEach((item) => {
			const activeChild = item.children.find((child) =>
				pathname.startsWith(child.path),
			);
			if (activeChild) {
				initialExpanded[item.path] = true;
			}
		});
		return initialExpanded;
	});
	const iconRefs = useRef<Record<string, HTMLSpanElement | null>>({});
	const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

	const getActiveChild = useCallback(
		(_parentPath: string, children: NavItem[]) => {
			return children.find((child) => pathname.startsWith(child.path)) || null;
		},
		[pathname],
	);

	const isRouteActive = (path: string) => {
		return pathname === path || pathname.startsWith(path + "/");
	};

	useEffect(() => {
		const updateRailX = (key: string) => {
			const icon = iconRefs.current[key];
			const container = submenuRefs.current[key];
			if (icon && container) {
				const containerRect = container.getBoundingClientRect();
				const iconRect = icon.getBoundingClientRect();
				const centerX = iconRect.left - containerRect.left + iconRect.width / 2;
				container.style.setProperty(`--${key}-rail-x`, `${centerX}px`);
			}
		};

		[...operationsDropdowns, ...managementDropdowns].forEach((item) => {
			updateRailX(item.path.replace(/\//g, "-").replace(/^-/, ""));
		});

		window.addEventListener("resize", () => {
			[...operationsDropdowns, ...managementDropdowns].forEach((item) => {
				updateRailX(item.path.replace(/\//g, "-").replace(/^-/, ""));
			});
		});

		return () => {
			window.removeEventListener("resize", () => {});
		};
	}, [isCollapsed, pathname, expandedDropdowns]);

	const toggleDropdown = (path: string) => {
		if (isCollapsed) {
			onExpand();
			const allDropdowns = [...operationsDropdowns, ...managementDropdowns];
			const activeChild = getActiveChild(
				path,
				allDropdowns.find((item) => item.path === path)?.children || [],
			);
			if (!activeChild) {
				setExpandedDropdowns((prev) => ({ ...prev, [path]: true }));
			}
			return;
		}
		setExpandedDropdowns((prev) => ({ ...prev, [path]: !prev[path] }));
	};

	const getDropdownKey = (path: string) => {
		return path.replace(/\//g, "-").replace(/^-/, "");
	};

	const getSectionTitle = () => {
		if (variant === "admin")
			return t("admin.dashboard.title") || "Panel de Administración";
		if (variant === "hub")
			return t("hub.dashboard.title") || "Portal del Inversor";
		return t("nav.operations");
	};

	const getNavItemClasses = (isActive: boolean) => {
		const baseClasses = `flex items-center rounded-md ${
			isCollapsed ? "w-10 h-10 justify-center" : "px-2 py-1.5 mx-2"
		}`;

		if (isActive) {
			return isDark
				? `${baseClasses} ${theme.activeBg} ${theme.text} shadow-md`
				: `${baseClasses} btn-active-nav`;
		}

		return isDark
			? `${baseClasses} ${theme.textSecondary} ${theme.hover} hover:${theme.text} hover:shadow-sm hover:scale-102 transition-all duration-200`
			: `${baseClasses} ${theme.textSecondary} hover:bg-hover-secondary hover:text-text-primary hover:shadow-sm hover:scale-102 transition-colors duration-200`;
	};

	return (
		<nav className="flex-1 py-3 overflow-y-auto">
			{/* Section Title */}
			{!isCollapsed && variant === "default" && (
				<div className="ml-7 mb-1 mt-4">
					<h3
						className={cn(
							"text-2xs font-light uppercase tracking-wider",
							theme.sectionTitle,
						)}
					>
						{t("nav.operations")}
					</h3>
				</div>
			)}

			{isDark && !isCollapsed && (
				<div className="ml-7 mb-4 mt-4">
					<h3
						className={cn(
							"text-2xs font-light uppercase tracking-wider",
							theme.sectionTitle,
						)}
					>
						{getSectionTitle()}
					</h3>
				</div>
			)}

			<ul className="space-y-0.5 px-3">
				{/* Single nav items */}
				{itemsToUse.map((item) => (
					<li key={item.path}>
						<NavLink
							to={item.path}
							className={({ isActive }) => getNavItemClasses(isActive)}
							onClick={(e) => e.stopPropagation()}
						>
							{({ isActive }) => (
								<>
									<span className="flex-shrink-0">{item.icon}</span>
									{!isCollapsed && (
										<span
											className={cn(
												"ml-2.5 text-s whitespace-nowrap",
												isActive ? "font-semibold" : "font-normal",
											)}
										>
											{item.label}
										</span>
									)}
								</>
							)}
						</NavLink>
					</li>
				))}

				{/* Dropdown nav items - Operations */}
				{operationsDropdowns.map((item) => {
					const dropdownKey = getDropdownKey(item.path);
					const isExpanded = expandedDropdowns[item.path] || false;
					const hasActiveChild =
						getActiveChild(item.path, item.children) !== null;

					return (
						<SidebarDropdownItem
							key={item.path}
							item={item}
							isCollapsed={isCollapsed}
							variant={variant}
							isExpanded={isExpanded}
							isActive={hasActiveChild}
							dropdownKey={dropdownKey}
							iconRefs={iconRefs}
							submenuRefs={submenuRefs}
							onToggle={toggleDropdown}
							isRouteActive={isRouteActive}
						/>
					);
				})}
			</ul>

			{/* Divider between Operations and Management (only for default variant) */}
			{variant === "default" && managementDropdowns.length > 0 && (
				<div className="mx-4 my-3">
					<div className="h-px bg-border-secondary opacity-80"></div>
				</div>
			)}

			{/* Management Section Title */}
			{variant === "default" &&
				!isCollapsed &&
				managementDropdowns.length > 0 && (
					<div className="ml-7 mb-1">
						<h3
							className={cn(
								"text-2xs font-light uppercase tracking-wider",
								theme.sectionTitle,
							)}
						>
							{t("nav.management")}
						</h3>
					</div>
				)}

			{/* Management dropdown items */}
			{variant === "default" && managementDropdowns.length > 0 && (
				<ul className="space-y-0.5 px-3">
					{managementDropdowns.map((item) => {
						const dropdownKey = getDropdownKey(item.path);
						const isExpanded = expandedDropdowns[item.path] || false;
						const hasActiveChild =
							getActiveChild(item.path, item.children) !== null;

						return (
							<SidebarDropdownItem
								key={item.path}
								item={item}
								isCollapsed={isCollapsed}
								variant={variant}
								isExpanded={isExpanded}
								isActive={hasActiveChild}
								dropdownKey={dropdownKey}
								iconRefs={iconRefs}
								submenuRefs={submenuRefs}
								onToggle={toggleDropdown}
								isRouteActive={isRouteActive}
							/>
						);
					})}
				</ul>
			)}
		</nav>
	);
};
